/**
 * C-09 取引先・現場（FR-11/12）／C-10 商品・単価（FR-10/14/17）。
 *
 * ★ 05 §6.1 C-09：「LINE発注」列は **「アプリ発注」に改称**（D-39）＝
 *   この列の意味は「この得意先はアプリから発注する」であって、トーク発注ではない。
 *
 * ★ T-D8（単票 UI）：一覧に「1件ずつ登録してください」と書いてあるのに登録する導線が無い、
 *   という状態を解消する（監査 masters#3）。**追加は admin・編集は staff も可**（03 §7）＝
 *   backend の POST=ConsoleAdmin / PATCH=ConsoleUser にそのまま合わせる。
 *   得意先の詳細（現場・個別単価・招待コード）は `CustomerDetail.tsx`。
 */

import { auth, console_, type Customer, type Product } from "@kenzai/api-client";
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  ErrorNote,
  Money,
  PageHeader,
  Spinner,
  Table,
  Td,
} from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Catalogs } from "./Catalogs";
import { Connections } from "./Connections";
import { InviteCodes } from "./InviteCodes";
import { CustomerPrices } from "./CustomerPrices";
import { CustomerForm, ProductForm } from "./masterForms";
import { adminOnlyNote, inputStyle } from "./formStyles";

/** URL クエリを1つだけ書き換える（他の条件を落とさない）。 */
function useQueryParam() {
  const [params, setParams] = useSearchParams();
  return {
    params,
    get: (k: string) => params.get(k) ?? "",
    set: (k: string, v: string) => {
      const next = new URLSearchParams(params);
      if (v) next.set(k, v);
      else next.delete(k);
      setParams(next);
    },
  };
}

export function Customers() {
  const qp = useQueryParam();
  const navigate = useNavigate();
  // D-79：招待コード（C-13）・接続（C-20）はサイドバーから外し、**取引先の下**に置いた。
  // 取引先ごとの操作は単票のタブ、**横断で見たいとき**はここのサブタブ
  // （「いま生きているコードはどれか」を一覧できる場所を失わない）。
  const crossTab = qp.get("tab");
  const q = qp.get("q");
  const includeDisabled = qp.get("disabled") === "1";
  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);

  const list = useQuery({
    queryKey: ["customers", q, includeDisabled],
    queryFn: () =>
      console_.customers(
        `?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}${includeDisabled ? "&include_disabled=true" : ""}`,
      ),
  });

  return (
    <>
      <PageHeader
        title="取引先・現場"
        subtitle={`得意先マスタ。1件ずつ登録するか、CSV で一括投入できます。${isAdmin ? "" : adminOnlyNote("追加・変更")}`}
        actions={
          // ★この見出しは道具が多い（タブ2＋検索＋トグル＋CSV＋追加）＝1行に収まらない幅がある。
          //   **折り返す**（`flexWrap`）＋**上端で揃える**（`alignItems`）ようにしておく。
          //   これが無いと、器（`.kz-tabs`）が縮められてタブの文字が途中で切れる。
          <div
            style={{
              display: "flex",
              gap: "var(--kz-space-2)",
              alignItems: "flex-start",
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {/* ★`.kz-tabs`（くぼんだ台）と `.kz-tab` は1組＝器を外すと裸のボタンの列になる */}
            {crossTab && (
              <div className="kz-tabs">
                <TabButton active={false} testid="tab-customers" onClick={() => qp.set("tab", "")}>
                  ← 取引先一覧
                </TabButton>
              </div>
            )}
            {!crossTab && (
              <div className="kz-tabs">
                <TabButton
                  active={crossTab === "invite-codes"}
                  testid="tab-invite-codes"
                  onClick={() => qp.set("tab", "invite-codes")}
                >
                  招待コード（全件）
                </TabButton>
                <TabButton
                  active={crossTab === "connections"}
                  testid="tab-connections"
                  onClick={() => qp.set("tab", "connections")}
                >
                  接続（全件）
                </TabButton>
              </div>
            )}
            {/* ★横断サブタブ（招待コード／接続）では**一覧用の道具を出さない**＝
                検索・利用停止トグル・CSV・追加はどれも取引先一覧にしか効かず、
                出したままだと「押しても何も起きない」ボタンが並ぶ（レビュー確定）。 */}
            {!crossTab && (
              <>
                <Search value={q} onChange={(v) => qp.set("q", v)} placeholder="取引先を検索" />
                <Toggle
                  testid="include-disabled"
                  checked={includeDisabled}
                  onChange={(v) => qp.set("disabled", v ? "1" : "")}
                  label="利用停止も表示"
                />
                {isAdmin && (
                  <Link to="/imports?kind=customers" className="kz-btn">
                    CSV で取り込む
                  </Link>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    data-testid="customer-new"
                    onClick={() => setCreating(true)}
                    className="kz-btn kz-btn--primary"
                  >
                    取引先を追加
                  </button>
                )}
              </>
            )}
          </div>
        }
      />
      {crossTab === "invite-codes" && <InviteCodes />}
      {crossTab === "connections" && <Connections />}
      {crossTab ? null : list.isLoading ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : (list.data?.items.length ?? 0) === 0 ? (
        <Empty
          title="取引先がありません"
          hint={
            isAdmin
              ? "「取引先を追加」から1件ずつ登録するか、CSV で取り込んでください。"
              // CSV 投入（適用）は admin 限定＝staff に案内すると 403 に着地する
              : "管理者に登録を依頼してください。"
          }
        />
      ) : (
        <Card padding="0">
          <Table head={["番号", "取引先", "担当者", "掛率", "アプリ発注", "状態", "備考", "操作"]}>
            {list.data!.items.map((c) => (
              <tr key={c.id} data-testid={`customer-${c.id}`}>
                <Td>
                  <span className="kz-num">{c.display_no}</span>
                </Td>
                <Td style={{ fontWeight: 600 }}>
                  {/* 現場・個別単価・招待コードは単票（C-09 詳細）にまとめる */}
                  <Link
                    to={`/masters/customers/${c.id}`}
                    data-testid={`customer-open-${c.id}`}
                    style={{ color: "var(--kz-color-primary)" }}
                  >
                    {c.name}
                  </Link>
                </Td>
                <Td>{c.person ?? "—"}</Td>
                <Td>
                  <span className="kz-num">{c.kake_rate ?? "—"}</span>
                </Td>
                <Td>
                  {/* D-39：**「LINE発注」ではない**＝アプリ（Web カタログ）からの発注 */}
                  {c.line_enabled ? <Badge tone="primary">あり</Badge> : <Badge>—</Badge>}
                </Td>
                <Td>{c.status === "active" ? <Badge tone="primary">利用中</Badge> : <Badge>利用停止</Badge>}</Td>
                <Td style={{ color: "var(--kz-color-ink-muted)" }}>{c.note ?? ""}</Td>
                <Td>
                  <button
                    type="button"
                    data-testid={`customer-edit-${c.id}`}
                    onClick={() => setEditing(c)}
                    className="kz-btn kz-btn--sm"
                  >
                    編集
                  </button>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {creating && (
        <CustomerForm
          // 作った直後は単票へ送る（現場と個別単価を続けて入れてもらう＝立ち上げの導線）
          onDone={(c) => {
            setCreating(false);
            navigate(`/masters/customers/${c.id}`);
          }}
          onCancel={() => setCreating(false)}
        />
      )}
      {editing && (
        <CustomerForm initial={editing} onDone={() => setEditing(null)} onCancel={() => setEditing(null)} />
      )}
    </>
  );
}

export function Products() {
  const qp = useQueryParam();
  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";
  const raw = qp.get("tab");
  // C-19 掲載管理は **C-10 の配下タブ**（D-75/D-79）＝「商品を登録する場所」と
  // 「アプリへの出し方を調整する場所」を1つの画面の中に並べる
  const tab = raw === "prices" ? "prices" : raw === "listing" ? "listing" : "list";

  return (
    <>
      <PageHeader
        title="商品・単価"
        subtitle={`商品マスタ。定価と発注点はここで管理します。${isAdmin ? "" : adminOnlyNote("追加・変更")}`}
        actions={
          <div className="kz-tabs">
            <TabButton active={tab === "list"} testid="tab-products" onClick={() => qp.set("tab", "")}>
              商品一覧
            </TabButton>
            <TabButton active={tab === "prices"} testid="tab-prices" onClick={() => qp.set("tab", "prices")}>
              個別単価
            </TabButton>
            <TabButton active={tab === "listing"} testid="tab-listing" onClick={() => qp.set("tab", "listing")}>
              掲載管理
            </TabButton>
          </div>
        }
      />
      {tab === "list" && <ProductList />}
      {tab === "prices" && <PricesByCustomer />}
      {tab === "listing" && <Catalogs embedded />}
    </>
  );
}

function ProductList() {
  const qp = useQueryParam();
  const q = qp.get("q");
  const category = qp.get("category");
  const includeDisabled = qp.get("disabled") === "1";
  const qc = useQueryClient();

  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";
  const meta = useQuery({ queryKey: ["masters-meta"], queryFn: () => console_.mastersMeta() });
  const list = useQuery({
    queryKey: ["products", q, category, includeDisabled],
    queryFn: () =>
      console_.products(
        `?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}${category ? `&category=${encodeURIComponent(category)}` : ""}${includeDisabled ? "&include_disabled=true" : ""}`,
      ),
  });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [toggling, setToggling] = useState<Product | null>(null);

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => console_.patchProduct(id, body),
    onSettled: (_data, _err, vars) => {
      setToggling(null);
      qc.invalidateQueries({ queryKey: ["products"] });
      // 単品キャッシュ（個別単価画面の「廃番」バッジ・定価）も落とす＝一覧キーとは別物
      if (vars) qc.invalidateQueries({ queryKey: ["product", vars.id] });
    },
  });

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "var(--kz-space-2)",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "var(--kz-space-4)",
        }}
      >
        <select className="kz-select"
          value={category}
          data-testid="category-filter"
          onChange={(e) => qp.set("category", e.target.value)}
          style={inputStyle}
        >
          <option value="">すべてのカテゴリ</option>
          {/* FR-82 の精神＝**選択肢も API から**（画面に商品分類をハードコードしない） */}
          {(meta.data?.categories ?? []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <Search value={q} onChange={(v) => qp.set("q", v)} placeholder="商品を検索" />
        <Toggle
          testid="include-disabled"
          checked={includeDisabled}
          onChange={(v) => qp.set("disabled", v ? "1" : "")}
          label="廃番も表示"
        />
        {isAdmin && (
          <Link to="/imports?kind=products" className="kz-btn">
            CSV で取り込む
          </Link>
        )}
        {isAdmin && (
          <button type="button" data-testid="product-new" onClick={() => setCreating(true)} className="kz-btn kz-btn--primary">
            商品を追加
          </button>
        )}
      </div>

      {patch.isError && <ErrorNote error={patch.error} />}
      {list.isLoading ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : (list.data?.items.length ?? 0) === 0 ? (
        <Empty
          title="商品がありません"
          hint={
            isAdmin
              ? "「商品を追加」から1件ずつ登録するか、CSV で取り込んでください。"
              : "管理者に登録を依頼してください。"
          }
        />
      ) : (
        <Card padding="0">
          <Table head={["番号", "商品名", "規格", "単位", "定価", "カテゴリ", "発注点", "状態", "操作"]}>
            {list.data!.items.map((p) => (
              <tr key={p.id} data-testid={`product-${p.id}`}>
                <Td>
                  <span className="kz-num">{p.display_no}</span>
                </Td>
                <Td style={{ fontWeight: 600 }}>{p.name}</Td>
                <Td>{p.spec ?? "—"}</Td>
                <Td>{p.unit}</Td>
                <Td style={{ whiteSpace: "nowrap" }}>
                  <span data-testid={`product-price-${p.id}`}>
                    <Money value={p.list_price} />
                  </span>
                </Td>
                <Td>{p.category ?? "—"}</Td>
                <Td>
                  <span className="kz-num">{p.reorder_point ? Number(p.reorder_point) : "—"}</span>
                </Td>
                <Td>
                  <span data-testid={`product-status-${p.id}`}>
                    {p.status === "active" ? <Badge tone="primary">取扱中</Badge> : <Badge tone="warn">廃番</Badge>}
                  </span>
                </Td>
                <Td>
                  <div style={{ display: "flex", gap: "var(--kz-space-2)" }}>
                    <button
                      type="button"
                      data-testid={`product-edit-${p.id}`}
                      onClick={() => setEditing(p)}
                      className="kz-btn kz-btn--sm"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      data-testid={`product-toggle-${p.id}`}
                      onClick={() => setToggling(p)}
                      className="kz-btn kz-btn--sm"
                    >
                      {p.status === "active" ? "廃番にする" : "取扱を再開する"}
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {creating && <ProductForm onDone={() => setCreating(false)} onCancel={() => setCreating(false)} />}
      {editing && (
        <ProductForm initial={editing} onDone={() => setEditing(null)} onCancel={() => setEditing(null)} />
      )}
      {toggling && (
        <ConfirmDialog
          title={toggling.status === "active" ? "この商品を廃番にしますか？" : "この商品の取扱を再開しますか？"}
          body={
            toggling.status === "active"
              ? "取引先の注文画面から消えます（カタログに入っていても表示されません）。すでに入っている注文はそのまま残ります。"
              : "取引先の注文画面に戻ります（カタログに入っている場合）。"
          }
          confirmLabel={toggling.status === "active" ? "廃番にする" : "取扱を再開する"}
          tone={toggling.status === "active" ? "danger" : "primary"}
          busy={patch.isPending}
          onConfirm={() =>
            patch.mutate({
              id: toggling.id,
              body: {
                status: toggling.status === "active" ? "disabled" : "active",
                updated_at: toggling.updated_at,
              },
            })
          }
          onCancel={() => setToggling(null)}
        />
      )}
    </>
  );
}

/**
 * 個別単価（第3層・FR-14）を C-10 からも触れるようにする（05 §6.1 の C-09 行が個別単価を含む）。
 *
 * **API は得意先スコープ**（`/customers/{id}/prices`）＝「この商品を安く出している得意先」を
 * 逆に引く手段は持っていない。だから**先に取引先を選ぶ**形にする（勝手に N+1 で全社走査しない）。
 */
function PricesByCustomer() {
  const qp = useQueryParam();
  const selected = Number(qp.get("customer_id")) || 0;
  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const customers = useQuery({
    queryKey: ["customers", "", false],
    queryFn: () => console_.customers("?limit=100"),
  });

  const items = customers.data?.items ?? [];
  const customer = items.find((c) => c.id === selected);

  return (
    <>
      <div style={{ display: "flex", gap: "var(--kz-space-2)", alignItems: "center", marginBottom: "var(--kz-space-4)" }}>
        <label style={{ fontSize: "var(--kz-font-size-sm)", fontWeight: 600 }}>取引先</label>
        <select className="kz-select"
          data-testid="price-customer"
          value={selected || ""}
          onChange={(e) => qp.set("customer_id", e.target.value)}
          style={inputStyle}
        >
          <option value="">選んでください</option>
          {items.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_no ? `${c.display_no} ` : ""}
              {c.name}
            </option>
          ))}
        </select>
        {customer && (
          <Link to={`/masters/customers/${customer.id}`} style={{ color: "var(--kz-color-primary)" }}>
            この取引先の詳細へ
          </Link>
        )}
      </div>
      {customers.isLoading ? (
        <Spinner />
      ) : customers.isError ? (
        <ErrorNote error={customers.error} />
      ) : items.length === 0 ? (
        <Empty title="取引先がありません" hint="先に「取引先・現場」で取引先を登録してください。" />
      ) : !customer ? (
        <Empty title="取引先を選んでください" hint="個別単価は取引先ごとに設定します。" />
      ) : (
        <Card>
          <CustomerPrices customer={customer} isAdmin={me.data?.role === "admin"} />
        </Card>
      )}
    </>
  );
}

/**
 * 画面の中の切り替えタブ（M1.7・S3）。
 *
 * ★**呼ぶ側は必ず `.kz-tabs` の器で包む**＝くぼんだ台の上で選択中だけが白く浮く、という
 *   セグメントタブの形はこの2つで1組。片方だけだと「枠線ボタンの並び」に戻り、
 *   選択が枠の色でしか分からなくなる（以前がその状態だった）。
 * ★`aria-pressed` は**残す**（選択状態を機械に伝える唯一の手掛かり。`.is-selected` は見た目専用）。
 */
export function TabButton({
  active,
  testid,
  onClick,
  children,
}: {
  active: boolean;
  testid: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      className={active ? "kz-tab is-selected" : "kz-tab"}
    >
      {children}
    </button>
  );
}

function Toggle({
  testid,
  checked,
  onChange,
  label,
}: {
  testid: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--kz-space-2)",
        fontSize: "var(--kz-font-size-sm)",
        whiteSpace: "nowrap",
      }}
    >
      <input
        type="checkbox"
        data-testid={testid}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18 }}
      />
      {label}
    </label>
  );
}

function Search({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <input
      type="search"
      defaultValue={value}
      placeholder={placeholder}
      data-testid="search"
      // **Enter で検索**（打つたびに URL を書き換えると履歴が1文字ずつ積もる）
      onKeyDown={(e) => e.key === "Enter" && onChange((e.target as HTMLInputElement).value)}
      style={{ ...inputStyle, width: 200 }}
    />
  );
}
